import { useEffect, useState } from "react";
import { usePageState } from "@/hooks/usePageState";
import { Link } from "react-router-dom";
import { Search, ChevronDown, Phone, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PageHeader, StatusBadge } from "@/components/common";
import { useFetch } from "@/hooks/useFetch";
import { fmtDate, money } from "@/lib/format";
import { cn } from "@/lib/utils";

const CustomerRow = ({ c, open, toggle }) => (
  <div className="border-b border-slate-100 last:border-0" data-testid={`customer-row-${c.key}`}>
    <button onClick={toggle} className="grid w-full grid-cols-[1fr_auto] items-center gap-3 px-4 py-3.5 text-left hover:bg-slate-50 md:grid-cols-[1.4fr_1fr_1.4fr_90px_110px_24px]" data-testid={`customer-toggle-${c.key}`}>
      <span className="font-semibold text-slate-900">{c.name}</span>
      <span className="hidden text-sm text-slate-600 md:block">{c.mobile}</span>
      <span className="hidden truncate text-sm text-slate-600 md:block">{c.email || "—"}</span>
      <span className="text-sm text-slate-600"><span className="font-mono font-semibold text-slate-900">{c.bookings.length}</span> job{c.bookings.length === 1 ? "" : "s"}</span>
      <span className="hidden text-right font-mono text-sm md:block">{money(c.total_value)}</span>
      <ChevronDown className={cn("hidden h-4 w-4 text-slate-400 transition-transform md:block", open && "rotate-180")} />
    </button>
    {open && (
      <div className="bg-slate-50/70 px-4 pb-4">
        <div className="flex flex-wrap gap-4 py-2 text-sm md:hidden">
          <a href={`tel:${c.mobile}`} className="inline-flex items-center gap-1 text-sky-700"><Phone className="h-3.5 w-3.5" />{c.mobile}</a>
          {c.email && <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 text-sky-700"><Mail className="h-3.5 w-3.5" />{c.email}</a>}
        </div>
        <ul className="space-y-1.5">
          {c.bookings.map((b) => (
            <li key={b.id}>
              <Link to={`/bookings/${b.id}`} className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm hover:border-sky-300" data-testid={`customer-booking-${b.job_number}`}>
                <span className="font-mono font-semibold text-sky-700">#{b.job_number}</span>
                <span className="w-28">{fmtDate(b.moving_date, "d MMM yyyy")}</span>
                <span className="flex-1 truncate text-slate-500">{b.pickup_address} → {b.delivery_address}</span>
                <StatusBadge status={b.status} />
              </Link>
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

export default function CustomersPage() {
  const [term, setTerm] = usePageState("term", "");
  const [q, setQ] = useState(term);
  const [open, setOpen] = useState(null);
  const { data } = useFetch("/customers", { q: term });

  useEffect(() => {
    const t = setTimeout(() => setTerm(q), 250);
    return () => clearTimeout(t);
  }, [q, setTerm]);

  const list = data || [];
  return (
    <div data-testid="customers-page">
      <PageHeader title="Customers" subtitle="Built automatically from bookings – grouped by mobile number." />
      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, mobile or email" className="h-10 bg-white pl-9" data-testid="customers-search-input" />
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="hidden grid-cols-[1.4fr_1fr_1.4fr_90px_110px_24px] gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500 md:grid">
          <span>Customer</span><span>Mobile</span><span>Email</span><span>Jobs</span><span className="text-right">Total value</span><span />
        </div>
        {list.length === 0 && <p className="p-8 text-center text-sm text-slate-500" data-testid="customers-empty">No customers yet. They appear here once a booking is saved.</p>}
        {list.map((c) => <CustomerRow key={c.key} c={c} open={open === c.key} toggle={() => setOpen(open === c.key ? null : c.key)} />)}
      </div>
    </div>
  );
}
